import { requestWithRefresh } from "@/lib/api"

export type OpsPropertyFilter = "all" | "needs_attention" | "stale_devices" | "job_failures"

export interface OpsLlmFilters {
  from?: number
  to?: number
  propertyId?: string
  surface?: string
  model?: string
  status?: "ok" | "error" | ""
}

export interface OpsLlmHealthFilters extends OpsLlmFilters {
  category?: string
  provider?: string
  outcome?: string
  promptKey?: string
  promptVersion?: string
  env?: string
  userRole?: string
  eventsLimit?: number
  cursor?: string
}

export interface OpsLlmGroupedTotals {
  calls: number
  cost_total_usd: number
  unique_properties: number
  unique_actors: number
  unique_surfaces: number
}

export interface OpsLlmGroupedRow {
  key: string
  label: string
  calls: number
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  cost_input_usd: number
  cost_output_usd: number
  cost_cache_usd: number
  cost_tools_usd: number
  cost_total_usd: number
  pct_of_total: number
}

export type LlmRoutingCategory = "guest" | "host" | "ops"

export interface LlmRouteSelection {
  provider: string
  model: string
}

export interface LlmResolvedRoute {
  property_id: string
  surface_key: string
  category: LlmRoutingCategory
  primary: LlmRouteSelection
  fallback: LlmRouteSelection
  kill_switch: boolean
  reason: string
  reason_chain: string[]
}

export interface OpsLlmRoutingSurface {
  surface_key: string
  category: LlmRoutingCategory
  name: string
  description: string
  default_primary: LlmRouteSelection
  default_fallback: LlmRouteSelection
  prompt_ids: string[]
  prompt_refs: Array<{ file: string; entrypoint: string }>
  call_sites: Array<{ file: string; function: string }>
  override: {
    primary_provider: string
    primary_model: string
    fallback_provider: string
    fallback_model: string
    kill_switch: boolean
    updated_at: string
    updated_by: string
  } | null
  resolved: LlmResolvedRoute
}

export interface OpsLlmRoutingCategoryState {
  category: LlmRoutingCategory
  override: {
    primary_provider: string
    primary_model: string
    fallback_provider: string
    fallback_model: string
    kill_switch: boolean
    updated_at: string
    updated_by: string
  } | null
  resolved: {
    primary: LlmRouteSelection
    fallback: LlmRouteSelection
    kill_switch: boolean
  }
  surface_count: number
  overridden_surface_count: number
}

export interface OpsLlmRoutingResponse {
  ok: boolean
  property_id?: string
  provider_models?: Record<string, string[]>
  summary?: {
    total_surfaces: number
    overridden_surfaces: number
    category_overrides: number
    kill_switches: Record<LlmRoutingCategory, boolean>
    tables_source: string
  }
  categories?: Record<LlmRoutingCategory, OpsLlmRoutingCategoryState>
  surfaces?: OpsLlmRoutingSurface[]
  needs_review?: Array<{
    key: string
    category: string
    description: string
    notes?: string[]
  }>
  error?: string
  code?: string
}

function buildLlmQuery(params: OpsLlmFilters & { limit?: number; cursor?: string } = {}) {
  const query = new URLSearchParams()
  if (Number.isFinite(Number(params.from))) query.set("from", String(Math.floor(Number(params.from))))
  if (Number.isFinite(Number(params.to))) query.set("to", String(Math.floor(Number(params.to))))
  if (params.propertyId) query.set("propertyId", params.propertyId)
  if (params.surface) query.set("surface", params.surface)
  if (params.model) query.set("model", params.model)
  if (params.status) query.set("status", params.status)
  if (Number.isFinite(Number(params.limit)) && Number(params.limit) > 0) query.set("limit", String(Math.floor(Number(params.limit))))
  if (params.cursor) query.set("cursor", params.cursor)
  const q = query.toString()
  return q ? `?${q}` : ""
}

function buildLlmHealthQuery(params: OpsLlmHealthFilters = {}) {
  const query = new URLSearchParams()
  if (Number.isFinite(Number(params.from))) query.set("from", String(Math.floor(Number(params.from))))
  if (Number.isFinite(Number(params.to))) query.set("to", String(Math.floor(Number(params.to))))
  if (params.propertyId) query.set("propertyId", params.propertyId)
  if (params.surface) query.set("surface", params.surface)
  if (params.category) query.set("category", params.category)
  if (params.provider) query.set("provider", params.provider)
  if (params.model) query.set("model", params.model)
  if (params.status) query.set("status", params.status)
  if (params.outcome) query.set("outcome", params.outcome)
  if (params.promptKey) query.set("prompt_key", params.promptKey)
  if (params.promptVersion) query.set("prompt_version", params.promptVersion)
  if (params.env) query.set("env", params.env)
  if (params.userRole) query.set("user_role", params.userRole)
  if (Number.isFinite(Number(params.eventsLimit)) && Number(params.eventsLimit) > 0) query.set("events_limit", String(Math.floor(Number(params.eventsLimit))))
  if (params.cursor) query.set("cursor", params.cursor)
  const q = query.toString()
  return q ? `?${q}` : ""
}

export const opsAdminApi = {
  getSummary() {
    return requestWithRefresh<{
      ok: boolean
      summary?: {
        properties: { total: number; active: number }
        devices: { seen_24h: number; stale_7d: number; never_seen: number }
        jobs: { last_run_ok: number; failures_24h: number }
        guidebook_sessions?: { logins_24h: number; failed_logins_24h: number } | null
        llm_usage?: { today_usd: number; last_7d_usd: number } | null
      }
    }>("/api/admin/summary")
  },

  search(q: string) {
    return requestWithRefresh<{
      ok: boolean
      query?: string
      properties?: Array<{ id: string; name: string; slug: string; host_email: string }>
      users?: Array<{ id: string; email: string; name: string; role: string; is_active: boolean }>
      devices?: Array<{ id: string; token_hash: string; property_id: string; last_seen_at: string; created_at: string }>
      bookings?: Array<{ id: string; property_id: string; property_name: string; booking_number: string; start_date: string; end_date: string; summary: string }>
    }>(`/api/admin/search?q=${encodeURIComponent(q)}`)
  },

  getProperties(filter: OpsPropertyFilter = "all") {
    return requestWithRefresh<{
      ok: boolean
      filter?: string
      properties?: Array<{
        property_id: string
        property_name: string
        property_short_id: string
        host_email: string
        pms_health: { status: string; provider: string; connected: boolean }
        calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
        places_health: { status: string; count: number }
        display_health: { status: string; last_heartbeat: string; device_id: string }
        guidebook_health: { status: string; enabled: boolean; last_login_at: string }
        last_render_at: string
        latest_job: { status: string; job_type: string; started_at: string; error_message: string } | null
        job_failures_24h: number
        open_issues: string[]
        issue_count: number
        controls: { updates_disabled: boolean; llm_disabled: boolean; updated_at: string; updated_by: string }
      }>
    }>(`/api/admin/properties?filter=${encodeURIComponent(filter)}`)
  },

  getProperty(propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      property?: {
        property_id: string
        property_name: string
        property_short_id: string
        host_email: string
        pms_health: { status: string; provider: string; connected: boolean }
        calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
        places_health: { status: string; count: number }
        display_health: { status: string; last_heartbeat: string; device_id: string }
        guidebook_health: { status: string; enabled: boolean; last_login_at: string }
        last_render_at: string
        open_issues: string[]
        issue_count: number
        controls: { updates_disabled: boolean; llm_disabled: boolean; updated_at: string; updated_by: string }
        key_ids: {
          property_id: string
          primary_listing_id: string
          primary_listing_name: string
          render_key: string
          pms_property_ref: string
        }
        health_checklist: {
          pms: { status: string; provider: string; connected: boolean }
          calendar: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
          display: { status: string; last_heartbeat: string; device_id: string }
          guidebook: { status: string; enabled: boolean; last_login_at: string }
        }
        latest_activity: Array<{ ts: string; type: string; label: string; status: string; message: string }>
      }
    }>(`/api/admin/property/${encodeURIComponent(propertyId)}`)
  },

  runPropertyAction(propertyId: string, actionType: string, payload?: Record<string, unknown>) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      controls?: { updates_disabled: boolean; llm_disabled: boolean; updated_at: string; updated_by: string }
      render_key?: string
      result?: Record<string, unknown>
    }>(`/api/admin/property/${encodeURIComponent(propertyId)}/action`, {
      method: "POST",
      body: JSON.stringify({ actionType, payload: payload || {} }),
    })
  },

  getJobRuns(params: { failedOnly?: boolean; type?: string; limit?: number } = {}) {
    const query = new URLSearchParams()
    if (params.failedOnly) query.set("failedOnly", "1")
    if (params.type) query.set("type", params.type)
    if (params.limit) query.set("limit", String(params.limit))
    const q = query.toString()
    return requestWithRefresh<{
      ok: boolean
      job_runs?: Array<{
        id: string
        time: string
        job_type: string
        property_id: string
        property_name: string
        status: string
        duration_ms: number
        error_snippet: string
        retryable: boolean
      }>
    }>(`/api/admin/job-runs${q ? `?${q}` : ""}`)
  },

  getErrors(limit = 30) {
    return requestWithRefresh<{
      ok: boolean
      errors?: Array<{
        time: string
        level: string
        scope: string
        property_id: string
        category: string
        message: string
        correlation_id: string
      }>
    }>(`/api/admin/errors?limit=${encodeURIComponent(String(limit))}`)
  },

  retryJob(jobId: string) {
    return requestWithRefresh<{ ok: boolean; error?: string; code?: string; job_id?: string }>(
      `/app/jobs/${encodeURIComponent(jobId)}/retry`,
      { method: "POST" },
    )
  },

  getLlmSummary(filters: OpsLlmFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      summary?: {
        range: { from: number; to: number }
        kpis: {
          cost_today_usd: number
          cost_last_7d_usd: number
          cost_mtd_usd: number
          input_tokens: number
          output_tokens: number
          cache_hit_rate_estimate: number
          calls_ok: number
          calls_error: number
        }
        totals: {
          calls: number
          input_tokens: number
          output_tokens: number
          cache_create_tokens: number
          cache_read_tokens: number
          web_search_requests: number
          code_exec_requests: number
        }
        daily: Array<{
          day: string
          calls: number
          input_tokens: number
          output_tokens: number
          cost_total_usd: number
        }>
      }
    }>(`/api/ops/llm/summary${buildLlmQuery(filters)}`)
  },

  getLlmByProperty(filters: OpsLlmFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      totals?: OpsLlmGroupedTotals
      rows?: OpsLlmGroupedRow[]
    }>(`/api/ops/llm/by-property${buildLlmQuery(filters)}`)
  },

  getLlmBySurface(filters: OpsLlmFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      totals?: OpsLlmGroupedTotals
      rows?: OpsLlmGroupedRow[]
    }>(`/api/ops/llm/by-surface${buildLlmQuery(filters)}`)
  },

  getLlmByModel(filters: OpsLlmFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      totals?: OpsLlmGroupedTotals
      rows?: OpsLlmGroupedRow[]
    }>(`/api/ops/llm/by-model${buildLlmQuery(filters)}`)
  },

  getLlmByUser(filters: OpsLlmFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      totals?: OpsLlmGroupedTotals
      rows?: Array<OpsLlmGroupedRow & { actor_type?: string; email?: string }>
    }>(`/api/ops/llm/by-user${buildLlmQuery(filters)}`)
  },

  getLlmEvents(params: OpsLlmFilters & { limit?: number; cursor?: string } = {}) {
    return requestWithRefresh<{
      ok: boolean
      events?: Array<{
        id: string
        ts: number
        property_id: string
        property_name: string
        booking_id: string
        actor_type: string
        actor_id: string
        surface: string
        message_type: string
        model: string
        mode: string
        cache_ttl: string
        inference_geo: string
        input_tokens: number
        output_tokens: number
        cache_create_tokens: number
        cache_read_tokens: number
        web_search_requests: number
        code_exec_requests: number
        latency_ms: number
        status: string
        error_type: string
        http_status: number
        prompt_version: string
        correlation_id: string
      }>
      has_more?: boolean
      next_cursor?: string
    }>(`/api/ops/llm/events${buildLlmQuery(params)}`)
  },

  getLlmHealthSummary(filters: OpsLlmHealthFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      range?: { from: number; to: number }
      filters?: Record<string, unknown>
      kpis?: {
        calls: number
        success_pct: number
        error_pct: number
        p95_latency_ms: number
        tokens: number
        cost_total_usd: number
        cache_hit_rate: number
      }
      trends?: {
        calls: Array<{ ts: number; value: number }>
        errors: Array<{ ts: number; value: number }>
        latency: Array<{ ts: number; value: number }>
        cost: Array<{ ts: number; value: number }>
        tokens: Array<{ ts: number; value: number }>
      }
      facets?: {
        categories: string[]
        surfaces: string[]
        providers: string[]
        models: string[]
        prompt_keys: string[]
        prompt_versions: string[]
        envs: string[]
        user_roles: string[]
      }
      error?: string
    }>(`/api/ops/llm/health/summary${buildLlmHealthQuery(filters)}`)
  },

  getLlmHealthBreakdown(filters: OpsLlmHealthFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      range?: { from: number; to: number }
      tables?: {
        top_surfaces_by_cost: Array<{ surface: string; calls: number; cost_total_usd: number }>
        top_surfaces_by_error_rate: Array<{ surface: string; calls: number; errors: number; error_rate_pct: number }>
        top_models_by_latency: Array<{ provider: string; model: string; calls: number; avg_latency_ms: number }>
        error_clusters: Array<{
          cluster_key: string
          env: string
          category: string
          surface: string
          provider: string
          model: string
          prompt_key: string
          prompt_version: string
          error_taxonomy: string
          error_type: string
          error_code: string
          sample_http_status: number
          sample_error_message: string
          count_total: number
          first_seen_ts: number
          last_seen_ts: number
          last_trace_id: string
        }>
      }
      error?: string
    }>(`/api/ops/llm/health/breakdown${buildLlmHealthQuery(filters)}`)
  },

  getLlmHealthEvents(filters: OpsLlmHealthFilters = {}) {
    return requestWithRefresh<{
      ok: boolean
      range?: { from: number; to: number }
      rows?: Array<{
        id: string
        ts: number
        trace_id: string
        chain_id: string
        parent_call_id: string
        attempt_index: number
        is_fallback: boolean
        env: string
        category: string
        surface: string
        provider: string
        model: string
        route_surface: string
        routing_reason: string
        trigger: string
        mode: string
        outcome: string
        status: string
        http_status: number
        error_taxonomy: string
        error_type: string
        error_message: string
        latency_total_ms: number
        latency_provider_ms: number
        input_tokens: number
        output_tokens: number
        cache_hit: boolean
        cache_mode: string
        freshness_state: string
        stale_reason: string
        prompt_key: string
        prompt_version: string
        property_id: string
        property_name: string
        booking_id: string
        user_role: string
        estimated_cost_usd: number
        request_fingerprint: string
        response_fingerprint: string
      }>
      pagination?: { has_more: boolean; next_cursor: string }
      error?: string
    }>(`/api/ops/llm/health/events${buildLlmHealthQuery(filters)}`)
  },

  async getLlmHealth(filters: OpsLlmHealthFilters = {}): Promise<any> {
    const [summary, breakdown, events] = await Promise.all([
      opsAdminApi.getLlmHealthSummary(filters),
      opsAdminApi.getLlmHealthBreakdown(filters),
      opsAdminApi.getLlmHealthEvents(filters),
    ])
    if (!summary.ok) return summary as {
      ok: false
      error?: string
      code?: string
    }
    if (!breakdown.ok) return breakdown as {
      ok: false
      error?: string
      code?: string
    }
    if (!events.ok) return events as {
      ok: false
      error?: string
      code?: string
    }
    return {
      ok: true,
      range: summary.range,
      kpis: summary.kpis,
      trends: summary.trends,
      facets: summary.facets || {
        categories: [],
        surfaces: [],
        providers: [],
        models: [],
        prompt_keys: [],
        prompt_versions: [],
        envs: [],
        user_roles: [],
      },
      tables: {
        top_surfaces_by_cost: breakdown.tables?.top_surfaces_by_cost || [],
        top_surfaces_by_error_rate: breakdown.tables?.top_surfaces_by_error_rate || [],
        top_models_by_latency: breakdown.tables?.top_models_by_latency || [],
        error_clusters: breakdown.tables?.error_clusters || [],
        recent_events: events.rows || [],
      },
      events: events.pagination || { has_more: false, next_cursor: "" },
    }
  },

  getLlmHealthTrace(params: { traceId?: string; chainId?: string; eventId?: string }) {
    if (params.traceId) {
      return requestWithRefresh<{
        ok: boolean
        trace_id?: string
        chain_id?: string
        surface?: string
        property_id?: string
        attempts?: Array<Record<string, unknown>>
        error?: string
      }>(`/api/ops/llm/health/trace/${encodeURIComponent(params.traceId)}`)
    }
    const query = new URLSearchParams()
    if (params.traceId) query.set("trace_id", params.traceId)
    if (params.chainId) query.set("chain_id", params.chainId)
    if (params.eventId) query.set("event_id", params.eventId)
    return requestWithRefresh<{
      ok: boolean
      trace_id?: string
      chain_id?: string
      surface?: string
      property_id?: string
      attempts?: Array<Record<string, unknown>>
      error?: string
    }>(`/api/ops/llm/health/trace?${query.toString()}`)
  },

  exportLlmHealthJson(filters: OpsLlmHealthFilters = {}) {
    const query = new URLSearchParams()
    const raw = buildLlmHealthQuery(filters).replace(/^\?/, "")
    if (raw) {
      for (const [key, value] of new URLSearchParams(raw).entries()) {
        query.set(key, value)
      }
    }
    query.set("format", "json")
    return requestWithRefresh<{
      ok: boolean
      exported_at?: string
      count?: number
      rows?: Array<Record<string, unknown>>
      error?: string
    }>(`/api/ops/llm/health/export?${query.toString()}`)
  },

  recomputeLlmHealthSurface(surface: string, propertyId: string) {
    return requestWithRefresh<{
      ok: boolean
      error?: string
      code?: string
      surface?: string
      property_id?: string
      recomputed?: boolean
    }>("/api/ops/llm/health/recompute", {
      method: "POST",
      body: JSON.stringify({ surface, property_id: propertyId }),
    })
  },

  getIntelligenceGateConfig() {
    return requestWithRefresh<{
      ok: boolean
      config?: {
        ritual_enabled: boolean
        cooldown_minutes: number
        guidebook_grace_ms: number
      }
      source?: string
    }>("/api/admin/intelligence-gate")
  },

  saveIntelligenceGateConfig(config: {
    ritual_enabled: boolean
    cooldown_minutes: number
    guidebook_grace_ms: number
  }) {
    return requestWithRefresh<{
      ok: boolean
      config?: {
        ritual_enabled: boolean
        cooldown_minutes: number
        guidebook_grace_ms: number
      }
      error?: string
    }>("/api/admin/intelligence-gate", {
      method: "PUT",
      body: JSON.stringify(config),
    })
  },

  getSubscriptions() {
    return requestWithRefresh<{
      ok: boolean
      subscriptions?: Array<{
        property_id: string
        property_name: string
        property_slug: string
        legacy_tier: string
        owner_email: string
        owner_name: string
        plan: string
        plan_status: string
        is_trialing: boolean
        trial_ends_at: string | null
        degraded: boolean
        subscription_id: string | null
        created_at: string
      }>
      total?: number
    }>("/api/admin/subscriptions")
  },

  getLlmRouting(propertyId: string) {
    return requestWithRefresh<OpsLlmRoutingResponse>(
      `/api/admin/llm-routing?property_id=${encodeURIComponent(propertyId)}`
    )
  },

  saveLlmRouting(payload: {
    property_id: string
    updates?: Array<{
      surface_key: string
      primary_provider?: string
      primary_model?: string
      fallback_provider?: string
      fallback_model?: string
      kill_switch?: boolean
    }>
    category_updates?: Array<{
      category: LlmRoutingCategory
      primary_provider?: string
      primary_model?: string
      fallback_provider?: string
      fallback_model?: string
      kill_switch?: boolean
    }>
    kill_switch_updates?: Array<{ category: LlmRoutingCategory; kill_switch?: boolean; enabled?: boolean }>
  }) {
    return requestWithRefresh<OpsLlmRoutingResponse>("/api/admin/llm-routing", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },

  getLlmByBooking(bookingId: string) {
    return requestWithRefresh<{
      ok: boolean
      booking_id?: string
      summary?: {
        calls: number
        input_tokens: number
        output_tokens: number
        cost_total_usd: number
      } | null
      events?: Array<{
        id: string
        ts: number
        property_id: string
        surface: string
        message_type: string
        model: string
        status: string
        input_tokens: number
        output_tokens: number
        cost_total_usd: number
      }>
    }>(`/api/ops/llm/by-booking?bookingId=${encodeURIComponent(bookingId)}`)
  },

  triggerLlmSync(days?: number) {
    const query = days ? `?days=${encodeURIComponent(String(days))}` : ""
    return requestWithRefresh<{
      ok: boolean
      error?: string
      retry_after?: number
      openai?: { days_synced: number; skipped: boolean }
      anthropic?: { days_synced: number; skipped: boolean }
      errors?: string[]
    }>(`/api/ops/llm/sync${query}`, { method: "POST" })
  },

  getLlmProviderCosts(params: {
    from?: string
    to?: string
    provider?: string
    breakdownType?: string
    topKeysLimit?: number
    dailyLimit?: number
    breakdownLimit?: number
    syncLogLimit?: number
  } = {}) {
    const query = new URLSearchParams()
    if (params.from) query.set("from", params.from)
    if (params.to) query.set("to", params.to)
    if (params.provider) query.set("provider", params.provider)
    if (params.breakdownType) query.set("breakdown_type", params.breakdownType)
    if (Number.isFinite(params.topKeysLimit)) query.set("top_keys_limit", String(params.topKeysLimit))
    if (Number.isFinite(params.dailyLimit)) query.set("daily_limit", String(params.dailyLimit))
    if (Number.isFinite(params.breakdownLimit)) query.set("breakdown_limit", String(params.breakdownLimit))
    if (Number.isFinite(params.syncLogLimit)) query.set("sync_log_limit", String(params.syncLogLimit))
    const q = query.toString()
    return requestWithRefresh<{
      ok: boolean
      source?: string
      range?: { from_day: string; to_day: string } | null
      filters?: { provider?: string; breakdown_type?: string }
      totals?: {
        total_usd: number
        days_with_data: number
        daily_avg_usd: number
        peak_day: string
        peak_day_cost_usd: number
      }
      providers?: Array<{
        provider: string
        total_usd: number
        share_pct: number
        days_with_data: number
        last_synced_at: string
      }>
      breakdown_types?: Array<{
        provider: string
        type: string
        total_usd: number
        days_with_data: number
        row_count: number
      }>
      top_breakdown_keys?: Array<{
        provider: string
        type: string
        key: string
        total_usd: number
        days_with_data: number
        row_count: number
      }>
      daily?: Array<{
        provider: string
        day: string
        amount_usd: number
        synced_at: string
      }>
      breakdowns?: Array<{
        provider: string
        day: string
        key: string
        type: string
        amount_usd: number
      }>
      sync_logs?: Array<{
        id: string
        provider: string
        started_at: string
        finished_at: string
        status: string
        days_synced: number
        error_message: string
        trigger_type: string
      }>
    }>(`/api/ops/llm/provider-costs${q ? `?${q}` : ""}`)
  },

  getLlmForecast() {
    return requestWithRefresh<{
      ok: boolean
      forecast?: {
        daily_run_rate_usd: number
        est_today_usd: number
        this_month_usd: number
        this_quarter_usd: number
        this_half_usd: number
        this_year_usd: number
        half_label: string
        spent_month_usd: number
        spent_quarter_usd: number
        spent_half_usd: number
        spent_year_usd: number
        lookback_days: number
        data_source: string
        computed_at: string
      }
    }>("/api/ops/llm/forecast")
  },

  // ---- AI Control Center endpoints ----

  getAiSummary() {
    return requestWithRefresh<{
      ok: boolean
      summary?: AiSummary
    }>("/api/ops/llm/ai-summary")
  },

  getAiSurfacePerformance(days = 30) {
    return requestWithRefresh<{
      ok: boolean
      rows?: AiSurfaceRow[]
      provider_total_usd?: number
      days?: number
    }>(`/api/ops/llm/surface-performance?days=${days}`)
  },

  getAiPropertyCosts() {
    return requestWithRefresh<{
      ok: boolean
      rows?: AiPropertyRow[]
    }>("/api/ops/llm/property-costs")
  },

  getAiModelOptimisation(days = 30) {
    return requestWithRefresh<{
      ok: boolean
      models?: AiModelGroup[]
      days?: number
    }>(`/api/ops/llm/model-optimisation?days=${days}`)
  },

  getAiGuardrailStatus() {
    return requestWithRefresh<{
      ok: boolean
      guardrails?: AiGuardrails
    }>("/api/ops/llm/guardrail-status")
  },

  // ---- Model Routing ----

  getModelRouting() {
    return requestWithRefresh<{
      ok: boolean
      surfaces?: RoutingSurface[]
      rules?: ModelRoutingRule[]
      audit?: ModelRoutingAuditEntry[]
      available_models?: string[]
    }>("/api/ops/llm/model-routing")
  },

  createModelRouting(data: {
    scope: string
    scope_key: string
    mode: string
    model: string
    provider: string
    fallback_model?: string
    fallback_provider?: string
    max_tokens?: number | null
    timeout_ms?: number | null
    change_reason: string
  }) {
    return requestWithRefresh<{ ok: boolean; id?: string; error?: string }>(
      "/api/ops/llm/model-routing",
      { method: "POST", body: JSON.stringify(data) }
    )
  },

  updateModelRouting(id: string, data: {
    model?: string
    provider?: string
    fallback_model?: string
    fallback_provider?: string
    is_active?: number
    max_tokens?: number | null
    timeout_ms?: number | null
    change_reason: string
  }) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/ops/llm/model-routing/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(data) }
    )
  },

  deleteModelRouting(id: string, changeReason?: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/ops/llm/model-routing/${encodeURIComponent(id)}`,
      { method: "DELETE", body: JSON.stringify({ change_reason: changeReason || "Override removed" }) }
    )
  },

  // ---- Experiments ----

  getExperiments() {
    return requestWithRefresh<{
      ok: boolean
      experiments?: Experiment[]
    }>("/api/ops/llm/experiments")
  },

  createExperiment(data: {
    name: string
    surface: string
    mode?: string
    variant_a_model: string
    variant_a_provider?: string
    variant_b_model: string
    variant_b_provider?: string
    traffic_pct_b?: number
    max_samples?: number
  }) {
    return requestWithRefresh<{ ok: boolean; id?: string; error?: string }>(
      "/api/ops/llm/experiments",
      { method: "POST", body: JSON.stringify(data) }
    )
  },

  getExperiment(id: string) {
    return requestWithRefresh<{
      ok: boolean
      experiment?: ExperimentDetail
      error?: string
    }>(`/api/ops/llm/experiments/${encodeURIComponent(id)}`)
  },

  updateExperiment(id: string, action: "start" | "pause" | "complete") {
    return requestWithRefresh<{ ok: boolean; status?: string; error?: string }>(
      `/api/ops/llm/experiments/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify({ action }) }
    )
  },

  deleteExperiment(id: string) {
    return requestWithRefresh<{ ok: boolean; error?: string }>(
      `/api/ops/llm/experiments/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    )
  },

  analyseExperiment(id: string) {
    return requestWithRefresh<{
      ok: boolean
      analysis_id?: string
      pairs_analysed?: number
      recommendation?: string
      error?: string
    }>(
      `/api/ops/llm/experiments/${encodeURIComponent(id)}/analyse`,
      { method: "POST" }
    )
  },

  promoteExperiment(id: string) {
    return requestWithRefresh<{
      ok: boolean
      promoted_model?: string
      variant?: string
      recommendation?: string
      error?: string
    }>(
      `/api/ops/llm/experiments/${encodeURIComponent(id)}/promote`,
      { method: "POST" }
    )
  },

  // ---- Orchestration ----

  getAiOrchestration() {
    return requestWithRefresh<{
      ok: boolean
      rows?: AiOrchestrationRow[]
    }>("/api/ops/llm/surface-orchestration")
  },

  // ---- User management ----

  getOpsUsers(params: { search?: string; active?: string; role?: string; cursor?: string; limit?: number } = {}) {
    const query = new URLSearchParams()
    if (params.search) query.set("search", params.search)
    if (params.active) query.set("active", params.active)
    if (params.role) query.set("role", params.role)
    if (params.cursor) query.set("cursor", params.cursor)
    if (params.limit) query.set("limit", String(params.limit))
    const q = query.toString()
    return requestWithRefresh<{
      ok: boolean
      users?: OpsUserRow[]
      has_more?: boolean
      next_cursor?: string | null
    }>(`/api/admin/users${q ? `?${q}` : ""}`)
  },

  getOpsUserDetail(userId: string) {
    return requestWithRefresh<{
      ok: boolean
      user?: OpsUserDetail
      properties?: OpsUserProperty[]
      sessions?: OpsUserSession[]
      recent_audit?: OpsUserAuditEntry[]
    }>(`/api/admin/users/${encodeURIComponent(userId)}`)
  },

  updateOpsUser(userId: string, data: { is_active?: boolean; role?: string; is_platform_admin?: boolean }) {
    return requestWithRefresh<{ ok: boolean; updated?: string[]; error?: string }>(
      `/api/admin/users/${encodeURIComponent(userId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    )
  },

  revokeOpsUserSessions(userId: string) {
    return requestWithRefresh<{ ok: boolean; revoked_count?: number; error?: string }>(
      `/api/admin/users/${encodeURIComponent(userId)}/revoke-sessions`,
      { method: "POST" }
    )
  },

  triggerSurfaceRecompute(surface: string, propertyId?: string) {
    return requestWithRefresh<{
      ok: boolean
      queued_count?: number
      property_ids?: string[]
      message?: string
      error?: string
    }>(
      "/api/ops/llm/surface-recompute",
      { method: "POST", body: JSON.stringify({ surface, property_id: propertyId }) }
    )
  },
}

// ---- AI Control Center types ----

export interface AiSummary {
  mtd_spend_usd: number
  monthly_budget_usd: number
  projected_month_usd: number
  budget_pct: number
  budget_status: "green" | "yellow" | "red"
  cost_per_property: number
  active_property_count: number
  cost_per_night: number
  guest_nights_mtd: number
  cost_per_night_by_tier: Record<string, { nights: number; cost_usd: number; cost_per_night: number }>
  mrr_usd: number
  ai_pct_of_mrr: number
  efficiency: {
    cache_util_pct: number
    model_fit_pct: number
    error_stability_pct: number
  }
  category_costs: {
    guest: number
    host: number
    ops: number
  }
  value: {
    guest_interactions: number
    host_insights: number
  }
  anomaly: {
    level: "amber" | "red"
    today_calls: number
    avg_daily_calls: number
    ratio: number
  } | null
  total_calls: number
  daily_costs: Array<{ day: string; cost_usd: number }>
  last_sync: {
    status: string
    finished_at: string
    trigger_type: string
  } | null
  computed_at: string
}

export interface AiSurfaceHealth {
  level: "healthy" | "warning" | "error" | "idle"
  label: string
  reason: string | null
}

export interface AiSurfaceModelBreakdown {
  model: string
  calls: number
  est_cost_usd: number
  avg_latency_ms: number
  error_rate_pct: number
}

export interface AiSurfaceRow {
  surface: string
  label: string
  current_model: string
  category: "guest" | "host" | "ops"
  category_label: string
  calls: number
  est_cost_usd: number
  cost_per_call_usd: number
  avg_latency_ms: number
  error_rate_pct: number
  health: AiSurfaceHealth
  models: AiSurfaceModelBreakdown[]
}

// ---- Orchestration types ----

export interface AiOrchestrationRow {
  surface: string
  category: string
  update_mode: "on_demand" | "scheduled" | "event_driven" | "hybrid"
  schedule_cron: string | null
  min_interval_s: number
  force_refresh_after_s: number
  invalidate_on: string[]
  batch_enabled: boolean
  batch_priority: number
  plan_gate: string
  latency_class: "sync" | "near_sync" | "async"
  cascade_invalidate: string[]
  surface_version: string
  // Aggregated state
  total_properties: number
  fresh_count: number
  stale_count: number
  computing_count: number
  failed_count: number
  freshness_pct: number | null
  last_computed: string | null
  last_invalidated: string | null
  stale_breakdown: { lt_24h: number; d1_3: number; gt_7d: number }
  stale_reasons: Array<{ event: string; count: number }>
}

export interface AiPropertyRow {
  property_id: string
  property_name: string
  calls: number
  est_cost_usd: number
  guest_nights: number
  cost_per_night: number
  tier: string
}

export interface AiModelGroup {
  model: string
  calls: number
  est_cost_usd: number
  surfaces: Array<{
    surface: string
    category: "guest" | "host" | "ops"
    category_label: string
    calls: number
    est_cost_usd: number
    avg_output_tokens: number
    error_count: number
    health: AiSurfaceHealth
  }>
}

export interface AiGuardrails {
  daily_limit: number
  disabled_properties: Array<{
    property_id: string
    property_name: string
    updated_at: string
  }>
  rate_limit_warnings: Array<{
    property_id: string
    property_name: string
    calls_today: number
    pct_of_limit: number
  }>
  gate_config: Array<{
    surface: string
    model: string
    enabled: number
    max_daily_calls: number
    updated_at: string
  }> | null
}

export interface ModelRoutingRule {
  id: string
  scope: "global" | "category" | "surface"
  scope_key: string
  mode: string
  model: string
  provider: string
  fallback_model: string
  fallback_provider: string
  max_tokens: number | null
  timeout_ms: number | null
  is_active: boolean
  updated_at: string
  updated_by: string
  change_reason: string
}

export interface ModelRoutingAuditEntry {
  id: string
  routing_id: string
  field_changed: string
  old_value: string | null
  new_value: string | null
  changed_by: string
  change_reason: string
  changed_at: string
}

export interface RoutingSurface {
  surface: string
  label: string
  description: string
  category: string
  category_label: string
  primary_mode: string
  model: string
  provider: string
  fallback_model: string
  fallback_provider: string
  resolved_from: "surface" | "category" | "global" | "hardcoded"
  routing_rule_id: string | null
  calls_7d: number
  avg_latency_ms: number
  errors_7d: number
  error_rate_7d: number
  health: "healthy" | "warning" | "error" | "idle"
  last_call_ts: number
  mode_overrides: Record<string, { model: string; provider: string; rule_id: string }>
}

// ---- Experiment types ----

export interface Experiment {
  id: string
  name: string
  surface: string
  mode: string
  variant_a_model: string
  variant_a_provider: string
  variant_b_model: string
  variant_b_provider: string
  traffic_pct_b: number
  status: "draft" | "running" | "paused" | "completed"
  max_samples: number
  created_at: string
  started_at: string
  ended_at: string
  created_by: string
  samples_a?: number
  samples_b?: number
  latest_analysis?: ExperimentAnalysis | null
}

export interface ExperimentOutput {
  id: string
  variant: "a" | "b"
  model: string
  property_id: string
  input_hash: string
  output_preview: string
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: string
  created_at: string
}

export interface ExperimentAnalysis {
  id: string
  experiment_id: string
  analyst_model: string
  variant_a_quality_score: number
  variant_b_quality_score: number
  accuracy_a: number
  accuracy_b: number
  actionability_a: number
  actionability_b: number
  tone_a: number
  tone_b: number
  completeness_a: number
  completeness_b: number
  guest_experience_comparison: string
  host_experience_comparison: string
  recommendation: "promote_b" | "keep_a" | "inconclusive"
  motivation: string
  pairs_analysed: number
  analysed_at: string
}

export interface ExperimentDetail extends Experiment {
  outputs?: ExperimentOutput[]
  analyses?: ExperimentAnalysis[]
  kpi_comparison?: Record<string, {
    count: number
    avg_latency_ms: number
    avg_input_tokens: number
    avg_output_tokens: number
    success_rate_pct: number
  }>
}

// ---- User management types ----

export interface OpsUserRow {
  id: string
  easy_id: string
  email: string
  name: string
  role: string
  is_active: boolean
  is_platform_admin: boolean
  property_count: number
  active_session_count: number
  created_at: string
  last_login_at: string
}

export interface OpsUserDetail {
  id: string
  easy_id: string
  email: string
  name: string
  role: string
  is_active: boolean
  is_platform_admin: boolean
  created_at: string
  updated_at: string
  last_login_at: string
  plan: string
  plan_status: string
  is_trialing: boolean
  trial_ends_at: string
}

export interface OpsUserProperty {
  id: string
  name: string
  slug: string
  tier: string
  is_active: boolean
  membership_role: string
  created_at: string
}

export interface OpsUserSession {
  id: string
  created_at: string
  expires_at: string
  is_revoked: boolean
  is_active: boolean
  ip_address: string
  user_agent: string
}

export interface OpsUserAuditEntry {
  action: string
  property_id: string
  params: string
  created_at: string
}
